import React, { useRef } from "react";

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
	label?: string;
	error?: string;
	fullWidth?: boolean;
}

const Input: React.FC<InputProps> = ({
	label,
	error,
	fullWidth = true,
	className = "",
	id,
	...props
}) => {
	const inputId =
		id ||
		(label ? `input-${label.toLowerCase().replace(/\s+/g, "-")}` : undefined);
	const inputRef = useRef<HTMLInputElement>(null);

	const baseInputClasses =
		"appearance-none border rounded-lg py-2.5 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed transition-all duration-200 bg-white shadow-sm hover:shadow-md";
	const errorInputClasses = error ? "border-red-400 focus:ring-red-500" : "border-gray-300";
	const widthClasses = fullWidth ? "w-full" : "";
	const timeInputClasses = props.type === "time" ? "select-none" : "";
	const inputClasses = `${baseInputClasses} ${errorInputClasses} ${widthClasses} ${timeInputClasses} ${className}`;

	const handleContainerClick = () => {
		if (inputRef.current && !props.disabled && props.type === "time") {
			inputRef.current.showPicker?.();
			inputRef.current.focus();
		}
	};

	return (
		<div 
			className={`${fullWidth ? "w-full" : ""} ${props.type === "time" ? "select-none" : ""}`}
			onClick={handleContainerClick}
			onMouseDown={(e) => {
				// Prevent text selection when clicking on container for time inputs
				if (props.type === "time" && e.target !== inputRef.current) {
					e.preventDefault();
				}
			}}
		>
			{label && (
				<label
					htmlFor={inputId}
					className={`block text-gray-700 text-sm font-semibold mb-2 ${props.type === "time" ? "cursor-pointer" : ""}`}
				>
					{label}
				</label>
			)}
			<input 
				ref={inputRef}
				id={inputId} 
				className={inputClasses}
				onSelect={props.type === "time" ? (e) => e.preventDefault() : undefined}
				onMouseDown={props.type === "time" ? (e) => {
					// Prevent text selection, but allow the picker to open
					if (e.target === inputRef.current) {
						e.preventDefault();
						inputRef.current?.showPicker?.();
						inputRef.current?.focus();
					}
				} : undefined}
				{...props} 
			/>
			{error && <p className="mt-1 text-sm text-red-600">{error}</p>}
		</div>
	);
};

export default Input;
